/**
 * 채널 타입 플랫폼 아이콘 컴포넌트
 * FA4에서 지원하지 않는 플랫폼(TikTok, Discord, Telegram, Bilibili, Chzzk, Soop, X)은
 * 인라인 SVG로 렌더링하고, 나머지는 FontAwesome 4 아이콘 사용
 * 툴팁은 공용 Tooltip 컴포넌트로 표시
 *
 * Badge mode (asBadge=true, platformIconStyle='badge'):
 *   Renders the actual brand SVG/FA icon in white on a brand-colored square,
 *   giving consistent square sizing while preserving platform identity.
 */
import React from 'react';
import Tooltip from '@/components/Tooltip';
import { FaBilibili } from 'react-icons/fa6';
import {
  getChannelTypeColor,
  getChannelTypeIcon,
  getChannelTypeIconOffset,
  getChannelTypeIconScale,
  getChannelTypeTitle,
  toGeneralPostType,
} from '@/lib/utils/channelPlatformMeta';
import { usePlatformIconStyle } from '@/lib/utils/platformIconStyle';

interface IconProps {
  size?: number;
  color?: string;
  style?: React.CSSProperties;
}

function getSquareBoxStyle(size: number, style?: React.CSSProperties): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: size,
    height: size,
    minWidth: size,
    minHeight: size,
    lineHeight: 1,
    flexShrink: 0,
    ...style,
  };
}

function getInnerIconStyle(style?: React.CSSProperties): React.CSSProperties {
  return {
    display: 'block',
    width: 'auto',
    height: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    verticalAlign: 'middle',
    ...style,
  };
}

function getOpticalScaleStyle(scale: number, size: number, offsetX = 0, offsetY = 0): React.CSSProperties {
  return {
    width: '100%',
    height: '100%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transform: `translate(${offsetX * size}px, ${offsetY * size}px) scale(${scale})`,
    transformOrigin: 'center',
  };
}

/* ── TikTok ── */
function TikTokIcon({ size: _size = 14, color = '#010101', style }: IconProps) {
  return (
    <svg viewBox="0 0 448 512" style={getInnerIconStyle(style)}>
      <path fill={color} d="M448 209.9a210.1 210.1 0 01-122.8-39.3v178.8A162.6 162.6 0 11185 188.3v89.9a74.6 74.6 0 1052.2 71.2V0h88a121 121 0 00122.8 120.1z" />
    </svg>
  );
}

/* ── Douyin ── */
function DouyinIcon({ size: _size = 14, color = '#4E0101', style }: IconProps) {
  return (
    <svg viewBox="0 0 448 512" style={getInnerIconStyle(style)}>
      <path fill={color} d="M448 209.9a210.1 210.1 0 01-122.8-39.3v178.8A162.6 162.6 0 11185 188.3v89.9a74.6 74.6 0 1052.2 71.2V0h88a121 121 0 00122.8 120.1z" />
    </svg>
  );
}

/* ── Discord ── */
function DiscordIcon({ size: _size = 14, color = '#5865f2', style }: IconProps) {
  return (
    <svg viewBox="0 0 640 512" style={getInnerIconStyle(style)}>
      <path fill={color} d="M524.5 69.8a1.5 1.5 0 00-.8-.7A485.1 485.1 0 00404.1 32a1.8 1.8 0 00-1.9.9 338 338 0 00-14.9 30.6 447.8 447.8 0 00-134.4 0 309.5 309.5 0 00-15.1-30.6 1.9 1.9 0 00-1.9-.9A483.7 483.7 0 00116.1 69.1a1.7 1.7 0 00-.8.7C39.1 183.7 18.2 294.7 28.4 404.4a2 2 0 00.8 1.4A487.7 487.7 0 00176 479.9a1.9 1.9 0 002.1-.7A348.2 348.2 0 00208.1 430.4a1.9 1.9 0 00-1-2.6 321.2 321.2 0 01-45.9-21.9 1.9 1.9 0 01-.2-3.1c3.1-2.3 6.2-4.7 9.1-7.1a1.8 1.8 0 011.9-.3c96.2 43.9 200.4 43.9 295.5 0a1.8 1.8 0 011.9.2c2.9 2.4 6 4.9 9.1 7.2a1.9 1.9 0 01-.1 3.1 301.2 301.2 0 01-46 21.8 1.9 1.9 0 00-1 2.6 391.1 391.1 0 0030 48.8 1.9 1.9 0 002.1.7A486 486 0 00610.7 405.7a1.9 1.9 0 00.8-1.4C623.7 277.6 590.9 167.5 524.5 69.8zM222.5 337.6c-29 0-52.8-26.6-52.8-59.2s23.4-59.2 52.8-59.2c29.7 0 53.3 26.8 52.8 59.2 0 32.6-23.4 59.2-52.8 59.2zm195.4 0c-29 0-52.8-26.6-52.8-59.2s23.4-59.2 52.8-59.2c29.7 0 53.3 26.8 52.8 59.2 0 32.6-23.1 59.2-52.8 59.2z" />
    </svg>
  );
}

/* ── Telegram ── */
function TelegramIcon({ size: _size = 14, color = '#229ed9', style }: IconProps) {
  return (
    <svg viewBox="0 0 496 512" style={getInnerIconStyle(style)}>
      <path fill={color} d="M248 8C111 8 0 119 0 256s111 248 248 248 248-111 248-248S385 8 248 8zm121.8 169.9l-40.7 191.8c-3 13.6-11.1 16.9-22.4 10.5l-62-45.7-29.9 28.8c-3.3 3.3-6.1 6.1-12.5 6.1l4.4-63.1 114.9-103.8c5-4.4-1.1-6.9-7.7-2.5l-142 89.4-61.2-19.1c-13.3-4.2-13.6-13.3 2.8-19.7l239.1-92.2c11.1-4 20.8 2.7 17.2 19.5z" />
    </svg>
  );
}

/* ── Bilibili ── */
function BilibiliIcon({ size: _size = 14, color = '#00AEEC', style }: IconProps) {
  return <FaBilibili color={color} style={getInnerIconStyle(style)} />;
}

/* ── X (Twitter) ── */
function XTwitterIcon({ size: _size = 14, color = '#000000', style }: IconProps) {
  return (
    <svg viewBox="0 0 512 512" style={getInnerIconStyle(style)}>
      <path fill={color} d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8l164.9-188.5L26.8 48h145l100.5 132.9L389.2 48zm-24.8 373.8h39.1L151.1 88h-42L364.4 421.8z" />
    </svg>
  );
}

/* ── Chzzk (원본 icomoon e905 글리프 기반) ── */
function ChzzkIcon({ size: _size = 14, color = '#00ffa3', style }: IconProps) {
  const isGray = color === '#888' || color === '#888888' || color === 'gray';
  return (
    <img
      src="/assets/images/chzzk.png"
      alt=""
      aria-hidden
      style={{ ...getInnerIconStyle(style), objectFit: 'contain', ...(isGray ? { filter: 'grayscale(1) opacity(0.6)' } : {}) }}
    />
  );
}

/* ── Soop (AfreecaTV, 원본 icomoon e906 글리프 기반) ── */
function SoopIcon({ size: _size = 14, color = '#0545b1', style }: IconProps) {
  return (
    <svg viewBox="0 0 1024 1024" style={getInnerIconStyle(style)}>
      <path fill={color} d="M702 696c-73.9 0-140.2-32.1-186.1-83.3a5.3 5.3 0 00-7.3 0C462.6 663.9 396.2 696 322 696c-138.1 0-250-112-250-250s112-250 250-250c74 0 140.4 32.1 186.4 83.4a5.3 5.3 0 007.3 0C561.6 228.1 628 196 702 196c138.1 0 250 112 250 250S840.1 696 702 696zM702 316c-40 0-73.4 19.3-93.9 41l-23.6 24.6c-4.1 3.7-8.3 7.2-12.5 10.4-16.5 11.9-37.2 19-59.5 19h-.6c-22.4 0-43.1-7.1-60-19.2-4.6-3.5-8.7-6.9-12.5-10.4l-23.5-24.6c-20.5-21.7-53.9-41-93.9-41-71.7 0-130 58.3-130 130s58.3 130 130 130c40 0 73.4-19.3 93.9-41l23.5-24.6c4.1-3.7 8.3-7.2 12.5-10.4 16.5-11.9 37.2-19 59.5-19h.6c22.4 0 43.1 7.1 60 19.2 4.6 3.5 8.7 6.9 12.5 10.4l23.6 24.6c20.5 21.7 53.9 41 93.9 41 71.7 0 130-58.3 130-130s-58.3-130-130-130z" />
    </svg>
  );
}

type SvgRenderer = (props: IconProps) => React.JSX.Element;

const SVG_ICONS: Record<string, SvgRenderer> = {
  tiktok: TikTokIcon,
  douyin: DouyinIcon,
  discord: DiscordIcon,
  telegram: TelegramIcon,
  bilibili: BilibiliIcon,
  twitter: XTwitterIcon,
  chzzk: ChzzkIcon,
  afreeca: SoopIcon,
};

/**
 * 채널 타입에 해당하는 아이콘을 렌더링
 * 별도 값이 없으면 플랫폼명을 기본 툴팁으로 사용한다.
 */
export default function ChannelTypeIcon({
  type,
  size = 14,
  color: colorOverride,
  style,
  tooltip,
  tooltipPlacement,
  className,
  asBadge,
  badgeSize = 'md',
}: {
  type: string;
  size?: number;
  color?: string;
  style?: React.CSSProperties;
  tooltip?: React.ReactNode;
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  /** When true, renders a brand-colored square with the white platform icon (badge mode)
   *  or falls back to the raw logo icon, depending on the current platformIconStyle setting. */
  asBadge?: boolean;
  /** Badge size when asBadge=true (default: 'md'). */
  badgeSize?: 'sm' | 'md' | 'lg';
}) {
  const platformIconStyle = usePlatformIconStyle();

  if (asBadge && platformIconStyle === 'badge') {
    // Badge mode: white square with brand-colored icon + light-gray border.
    const badgePx = badgeSize === 'sm' ? 14 : badgeSize === 'lg' ? 26 : 20;
    const iconPx = Math.round(badgePx * 0.65);
    const brandColor = getChannelTypeColor(type);
    const tooltipContent = tooltip === undefined ? getChannelTypeTitle(type) : tooltip;
    const SvgComp = SVG_ICONS[toGeneralPostType(type)];

    return (
      <Tooltip content={tooltipContent} placement={tooltipPlacement} avoidCollisions={tooltipPlacement ? false : undefined}>
        <span
          className={className}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: badgePx,
            height: badgePx,
            minWidth: badgePx,
            minHeight: badgePx,
            borderRadius: 4,
            backgroundColor: '#ffffff',
            border: '1px solid #ffffff',
            // border: '1px solid #e2e4e9',
            flexShrink: 0,
          }}
        >
          {SvgComp ? (
            <span style={getSquareBoxStyle(iconPx)}>
              <SvgComp size={iconPx} color={brandColor} />
            </span>
          ) : (
            <i
              className={`fa ${getChannelTypeIcon(type)}`}
              style={{ color: brandColor, fontSize: iconPx, lineHeight: 1 }}
            />
          )}
        </span>
      </Tooltip>
    );
  }

  const color = colorOverride ?? getChannelTypeColor(type);
  const tooltipContent = tooltip === undefined ? getChannelTypeTitle(type) : tooltip;
  const iconScale = getChannelTypeIconScale(type);
  const iconOffset = getChannelTypeIconOffset(type);

  const SvgComponent = SVG_ICONS[toGeneralPostType(type)];
  if (SvgComponent) {
    return (
      <Tooltip content={tooltipContent} placement={tooltipPlacement} avoidCollisions={tooltipPlacement ? false : undefined}>
        <span className={className} style={getSquareBoxStyle(size, style)}>
          <span style={getOpticalScaleStyle(iconScale, size, iconOffset.x, iconOffset.y)}>
            <SvgComponent size={size} color={color} />
          </span>
        </span>
      </Tooltip>
    );
  }

  const faIcon = getChannelTypeIcon(type);
  return (
    <Tooltip content={tooltipContent} placement={tooltipPlacement} avoidCollisions={tooltipPlacement ? false : undefined}>
      <span className={className} style={getSquareBoxStyle(size, style)}>
        <span style={getOpticalScaleStyle(iconScale, size, iconOffset.x, iconOffset.y)}>
          <i
            className={`fa ${faIcon}`}
            style={{ color, fontSize: size, lineHeight: 1, verticalAlign: 'middle' }}
          />
        </span>
      </span>
    </Tooltip>
  );
}
